import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Address, type DeviceModel, type DeviceUnit, type Installation, type KnownAddress, type Participant } from '@regigov/contracts';
import { ApiError } from '../api/client';
import { addressesApi, installationsApi, modelsApi, rulesApi, unitsApi } from '../api/devices';
import { participantsApi } from '../api/participants';
import { AttributesForm, type Attributes } from './AttributesForm';
import { Field, ProblemAlert } from './ui';

export type FormMode = 'cer' | 'participant';

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Field-level messages from a problem-details response, keyed by field path. */
function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !error.problem.errors) return {};
  const out: Record<string, string> = {};
  for (const e of error.problem.errors) if (e.field && !out[e.field]) out[e.field] = e.message;
  return out;
}

function ParticipantSelect({ label, value, onChange, types, error }: { label: string; value: string; onChange: (id: string) => void; types: string[]; error?: string }) {
  const list = useQuery({ queryKey: ['participants', '', 'approved'], queryFn: () => participantsApi.search('', 'approved') });
  const options = (list.data?.items ?? []).filter((p: Participant) => types.includes(p.participantType));
  return (
    <Field label={label} hint={error ?? `Approved ${types.join('/')} participants only`}>
      <select value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">Select…</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.legalName} ({p.participantType})
          </option>
        ))}
      </select>
    </Field>
  );
}

function RuleNotice({ deviceClass, on, kind }: { deviceClass: string; on: string; kind: 'model' | 'unit' | 'installation' }) {
  const rule = useQuery({ queryKey: ['rules', 'current', deviceClass, on], queryFn: () => rulesApi.current(deviceClass, on), enabled: !!deviceClass, retry: false });
  if (!deviceClass) return null;
  if (rule.error) return <ProblemAlert error={rule.error} />;
  if (!rule.data) return null;
  return (
    <p className="notice">
      Validating {kind} attributes against <strong>{rule.data.displayName}</strong> rules v{rule.data.version} (in force from {rule.data.validFrom}
      {rule.data.validTo ? ` to ${rule.data.validTo}` : ''}).
    </p>
  );
}

// ---- Model ------------------------------------------------------------------

export function ModelForm({ mode, onSaved }: { mode: FormMode; onSaved: (m: DeviceModel) => void }) {
  const classes = useQuery({ queryKey: ['rules', 'classes'], queryFn: rulesApi.classes });
  const [manufacturerId, setManufacturerId] = useState('');
  const [deviceClass, setDeviceClass] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [name, setName] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [attributes, setAttributes] = useState<Attributes>({});
  const rule = useQuery({ queryKey: ['rules', 'current', deviceClass, effectiveDate], queryFn: () => rulesApi.current(deviceClass, effectiveDate), enabled: !!deviceClass, retry: false });
  const save = useMutation({
    mutationFn: () => modelsApi.register({ manufacturerId: mode === 'cer' ? manufacturerId : undefined, deviceClass, modelNumber, name, attributes, effectiveDate }),
    onSuccess: onSaved,
  });
  const errors = useMemo(() => fieldErrors(save.error), [save.error]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <form className="form" onSubmit={submit}>
      <div className="grid2">
        {mode === 'cer' && <ParticipantSelect label="Manufacturer" value={manufacturerId} onChange={setManufacturerId} types={['manufacturer', 'importer']} error={errors.manufacturerId} />}
        <Field label="Device class" hint={errors.deviceClass}>
          <select
            value={deviceClass}
            onChange={(e) => {
              setDeviceClass(e.target.value);
              setAttributes({});
            }}
            required
          >
            <option value="">Select…</option>
            {(classes.data?.items ?? []).map((c) => (
              <option key={c.deviceClass} value={c.deviceClass}>
                {c.displayName} ({c.deviceClass})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Model number" hint={errors.modelNumber ?? 'Unique per manufacturer (case-insensitive)'}>
          <input value={modelNumber} onChange={(e) => setModelNumber(e.target.value)} required maxLength={100} />
        </Field>
        <Field label="Model name" hint={errors.name}>
          <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={200} />
        </Field>
        <Field label="Effective date" hint="Rules in force on this date apply">
          <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} required />
        </Field>
      </div>
      <RuleNotice deviceClass={deviceClass} on={effectiveDate} kind="model" />
      {rule.data && <AttributesForm key={rule.data.id} schema={rule.data.fieldSchema.model} value={attributes} onChange={setAttributes} errors={errors} />}
      <ProblemAlert error={save.error} />
      <div className="row">
        <button type="submit" disabled={save.isPending || !rule.data}>
          Register model
        </button>
        {mode === 'participant' && <span className="muted">Models you submit are pending until CER approves them.</span>}
      </div>
    </form>
  );
}

// ---- Unit -------------------------------------------------------------------

export function UnitForm({ mode, initialModelId, onSaved }: { mode: FormMode; initialModelId?: string; onSaved: (u: DeviceUnit) => void }) {
  const models = useQuery({ queryKey: ['models', { status: 'approved', catalogue: 'true' }], queryFn: () => modelsApi.search({ status: 'approved', catalogue: 'true' }) });
  const [modelId, setModelId] = useState(initialModelId ?? '');
  const [ownerParticipantId, setOwnerParticipantId] = useState('');
  const [serial, setSerial] = useState('');
  const [batch, setBatch] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [attributes, setAttributes] = useState<Attributes>({});
  const model = models.data?.items.find((m) => m.id === modelId);
  const deviceClass = model?.deviceClass ?? '';
  const rule = useQuery({ queryKey: ['rules', 'current', deviceClass, effectiveDate], queryFn: () => rulesApi.current(deviceClass, effectiveDate), enabled: !!deviceClass, retry: false });
  const save = useMutation({
    mutationFn: () => unitsApi.register({ modelId, ownerParticipantId: mode === 'cer' ? ownerParticipantId : undefined, serial, batch: batch || undefined, attributes, effectiveDate }),
    onSuccess: onSaved,
  });
  const errors = useMemo(() => fieldErrors(save.error), [save.error]);

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid2">
        <Field label="Model" hint={errors.modelId ?? 'Approved models only'}>
          <select
            value={modelId}
            onChange={(e) => {
              setModelId(e.target.value);
              setAttributes({});
            }}
            required
          >
            <option value="">Select…</option>
            {(models.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.manufacturerName} — {m.modelNumber} ({m.name})
              </option>
            ))}
          </select>
        </Field>
        {mode === 'cer' && (
          <ParticipantSelect label="Owning participant" value={ownerParticipantId} onChange={setOwnerParticipantId} types={['manufacturer', 'importer', 'installer', 'retailer', 'aggregator']} error={errors.ownerParticipantId} />
        )}
        <Field label="Serial number" hint={errors.serial ?? 'Unique per model'}>
          <input value={serial} onChange={(e) => setSerial(e.target.value)} required maxLength={100} />
        </Field>
        <Field label="Batch" hint={errors.batch ?? 'Optional; used to scope recalls'}>
          <input value={batch} onChange={(e) => setBatch(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Effective date" hint="Rules in force on this date apply">
          <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} required />
        </Field>
      </div>
      <RuleNotice deviceClass={deviceClass} on={effectiveDate} kind="unit" />
      {rule.data && <AttributesForm key={rule.data.id} schema={rule.data.fieldSchema.unit} value={attributes} onChange={setAttributes} errors={errors} />}
      <ProblemAlert error={save.error} />
      <div className="row">
        <button type="submit" disabled={save.isPending || !rule.data}>
          Register unit
        </button>
      </div>
    </form>
  );
}

// ---- Installation -----------------------------------------------------------

const STATES = Address.shape.state.options;

export function InstallationForm({ mode, initialUnitId, onSaved }: { mode: FormMode; initialUnitId?: string; onSaved: (i: Installation) => void }) {
  const [unitQuery, setUnitQuery] = useState('');
  const units = useQuery({ queryKey: ['units', { q: unitQuery, status: 'registered' }], queryFn: () => unitsApi.search({ q: unitQuery, status: 'registered' }) });
  const initialUnit = useQuery({ queryKey: ['units', 'detail', initialUnitId], queryFn: () => unitsApi.get(initialUnitId!), enabled: !!initialUnitId });
  const [unitId, setUnitId] = useState(initialUnitId ?? '');
  const [installerParticipantId, setInstallerParticipantId] = useState('');
  const [nmi, setNmi] = useState('');
  const [commissionedOn, setCommissionedOn] = useState(todayIso());
  const [address, setAddress] = useState<Address>({ line1: '', suburb: '', state: 'NSW', postcode: '' });
  const [attributes, setAttributes] = useState<Attributes>({});
  const candidates = [...(units.data?.items ?? [])];
  if (initialUnit.data && !candidates.some((u) => u.id === initialUnit.data!.id)) candidates.unshift(initialUnit.data);
  const unit = candidates.find((u) => u.id === unitId);
  const deviceClass = unit?.deviceClass ?? '';
  const rule = useQuery({ queryKey: ['rules', 'current', deviceClass, commissionedOn], queryFn: () => rulesApi.current(deviceClass, commissionedOn), enabled: !!deviceClass, retry: false });
  const save = useMutation({
    mutationFn: () => installationsApi.register({ unitId, installerParticipantId: mode === 'cer' ? installerParticipantId : undefined, nmi: nmi.toUpperCase(), address, commissionedOn, attributes }),
    onSuccess: onSaved,
  });
  const errors = useMemo(() => fieldErrors(save.error), [save.error]);
  const [picked, setPicked] = useState<KnownAddress | null>(null);
  const suggestions = useQuery({ queryKey: ['addresses', address.line1], queryFn: () => addressesApi.search(address.line1), enabled: address.line1.trim().length >= 3 && !picked });
  const setAddr = (k: keyof Address, v: string) => {
    setPicked(null);
    setAddress({ ...address, [k]: v });
  };
  const pick = (k: KnownAddress) => {
    setPicked(k);
    setAddress({ line1: k.line1, ...(k.line2 ? { line2: k.line2 } : {}), suburb: k.suburb, state: k.state, postcode: k.postcode });
  };

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid2">
        <Field label="Find unit" hint="Serial, batch or model; only units not currently installed are listed">
          <input value={unitQuery} onChange={(e) => setUnitQuery(e.target.value)} placeholder="Serial number" />
        </Field>
        <Field label="Unit" hint={errors.unitId}>
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)} required>
            <option value="">Select…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.serial} — {u.modelNumber} ({u.ownerName})
              </option>
            ))}
          </select>
        </Field>
        {mode === 'cer' && <ParticipantSelect label="Installer" value={installerParticipantId} onChange={setInstallerParticipantId} types={['installer', 'retailer']} error={errors.installerParticipantId} />}
        <Field label="NMI" hint={errors.nmi ?? '10–11 characters'}>
          <input value={nmi} onChange={(e) => setNmi(e.target.value.toUpperCase())} required minLength={10} maxLength={11} />
        </Field>
        <Field label="Commissioned on" hint={errors.commissionedOn ?? 'Rules in force on this date apply'}>
          <input type="date" value={commissionedOn} max={todayIso()} onChange={(e) => setCommissionedOn(e.target.value)} required />
        </Field>
      </div>
      <fieldset>
        <legend>Site address</legend>
        <div className="grid2">
          <Field label="Address line 1" hint={errors['address.line1']}>
            <input value={address.line1} onChange={(e) => setAddr('line1', e.target.value)} required autoComplete="off" />
          </Field>
          <Field label="Suburb" hint={errors['address.suburb']}>
            <input value={address.suburb} onChange={(e) => setAddr('suburb', e.target.value)} required />
          </Field>
          <Field label="State">
            <select value={address.state} onChange={(e) => setAddr('state', e.target.value)}>
              {STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Postcode" hint={errors['address.postcode']}>
            <input value={address.postcode} onChange={(e) => setAddr('postcode', e.target.value)} required pattern="\d{4}" />
          </Field>
        </div>
        {!picked && suggestions.data && suggestions.data.items.length > 0 && (
          <div>
            <p className="muted">Known addresses:</p>
            <ul>
              {suggestions.data.items.map((k) => (
                <li key={k.id}>
                  <button type="button" className="link" onClick={() => pick(k)}>
                    {k.line1}
                    {k.line2 ? `, ${k.line2}` : ''}, {k.suburb} {k.state} {k.postcode}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="muted">
          {picked
            ? `Known address: located at ${picked.lat.toFixed(5)}, ${picked.lon.toFixed(5)} (${picked.source}, ${picked.accuracy}).`
            : 'RegiGov looks the address up in its known-address register. If it is not there, the installation is recorded as Not geocoded for CER follow-up.'}
        </p>
      </fieldset>
      <RuleNotice deviceClass={deviceClass} on={commissionedOn} kind="installation" />
      {rule.data && <AttributesForm key={rule.data.id} schema={rule.data.fieldSchema.installation} value={attributes} onChange={setAttributes} errors={errors} />}
      <ProblemAlert error={save.error} />
      <div className="row">
        <button type="submit" disabled={save.isPending || !rule.data}>
          Record installation
        </button>
      </div>
    </form>
  );
}

/** Ownership transfer: the current owner (or CER) hands the unit to another approved participant. */
export function TransferForm({ unit, mode, onTransferred }: { unit: DeviceUnit; mode: FormMode; onTransferred: (u: DeviceUnit) => void }) {
  const [newOwnerParticipantId, setNewOwner] = useState('');
  const [changeReason, setReason] = useState('');
  const [evidenceRef, setEvidence] = useState('');
  const mutation = useMutation({
    mutationFn: () => unitsApi.transfer(unit.id, { newOwnerParticipantId, changeReason, evidenceRef: evidenceRef || undefined }),
    onSuccess: (u) => {
      setNewOwner('');
      setReason('');
      setEvidence('');
      onTransferred(u);
    },
  });
  const errors = fieldErrors(mutation.error);
  return (
    <details className="panel">
      <summary>Transfer ownership</summary>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="grid2">
          {mode === 'cer' ? (
            <ParticipantSelect label="New owner" value={newOwnerParticipantId} onChange={setNewOwner} types={['manufacturer', 'importer', 'installer', 'retailer', 'aggregator']} error={errors.newOwnerParticipantId} />
          ) : (
            <Field label="New owner participant ID" hint={errors.newOwnerParticipantId ?? 'CER-P-… of an approved participant'}>
              <input value={newOwnerParticipantId} onChange={(e) => setNewOwner(e.target.value.trim())} required pattern="CER-P-[0-9A-Z]{26}" />
            </Field>
          )}
          <Field label="Evidence reference" hint="e.g. invoice or contract number">
            <input value={evidenceRef} onChange={(e) => setEvidence(e.target.value)} />
          </Field>
        </div>
        <Field label="Reason" hint="Recorded in the unit's history">
          <textarea value={changeReason} onChange={(e) => setReason(e.target.value)} required minLength={3} rows={2} />
        </Field>
        <ProblemAlert error={mutation.error} />
        <div>
          <button type="submit" disabled={mutation.isPending}>
            Transfer
          </button>
        </div>
      </form>
    </details>
  );
}
